import { Check, MessageCircle, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { CanvasCommentRow } from "@/lib/api";
import { cn } from "@/lib/utils";

interface CanvasCommentsPanelProps {
	isOpen: boolean;
	onClose: () => void;
	comments: CanvasCommentRow[];
	activeCommentId: number | null;
	editingCommentId: number | null;
	editingText: string;
	onEditingTextChange: (value: string) => void;
	onSaveEdit: () => void;
	onCancelEdit: () => void;
	onFocusComment: (comment: CanvasCommentRow) => void;
	onResolveComment: (commentId: number) => void;
}

/** Right sidebar listing canvas comments (visible in comment mode). */
export function CanvasCommentsPanel({
	isOpen,
	onClose,
	comments,
	activeCommentId,
	editingCommentId,
	editingText,
	onEditingTextChange,
	onSaveEdit,
	onCancelEdit,
	onFocusComment: handleFocusComment,
	onResolveComment: handleResolveComment,
}: CanvasCommentsPanelProps) {
	return (
		<AnimatePresence>
			{isOpen && (
				<motion.aside
					initial={{ width: 0, opacity: 0 }}
					animate={{ width: 320, opacity: 1 }}
					exit={{ width: 0, opacity: 0 }}
					transition={{ duration: 0.3, ease: "easeInOut" }}
					className="flex-shrink-0 mt-4 mb-4 rounded-2xl mr-4 border border-border-subtle bg-bg-surface flex flex-col overflow-hidden"
				>
					{/* Sidebar Header */}
					<div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-border-subtle">
						<div className="flex items-center gap-2">
							<MessageCircle size={16} className="text-text-primary" />
							<h2 className="text-base font-semibold text-text-primary">
								Comments
							</h2>
							{comments.length > 0 && (
								<span className="ml-1 text-2xs font-medium text-text-tertiary bg-bg-canvas px-2 py-0.5 rounded-full">
									{comments.length}
								</span>
							)}
						</div>
						<Button variant="ghost" size="icon-xs" onClick={onClose}>
							<X size={16} />
						</Button>
					</div>

					{/* Comment List */}
					<div className="flex-1 overflow-y-auto scrollbar-thin">
						{comments.length === 0 ? (
							<div className="flex flex-col items-center justify-center py-16 px-6">
								<div className="w-12 h-12 rounded-full bg-bg-canvas flex items-center justify-center mb-3">
									<MessageCircle size={20} className="text-text-tertiary" />
								</div>
								<p className="text-sm text-text-tertiary text-center leading-relaxed">
									No comments yet.
								</p>
								<p className="text-2xs text-text-tertiary text-center mt-1">
									Click anywhere on the canvas to add one.
								</p>
							</div>
						) : (
							<div className="py-1">
								{comments.map((comment, idx) => (
									<div
										key={comment.id}
										className={cn(
											"w-full text-left px-4 py-3 transition-colors group",
											activeCommentId === comment.id
												? "bg-bg-sunken"
												: "hover:bg-bg-sunken/50",
										)}
									>
										<div className="flex items-start gap-3">
											{/* Numbered marker — click to focus */}
											<button
												onClick={() => handleFocusComment(comment)}
												className={cn(
													"w-7 h-7 rounded-full flex items-center justify-center text-2xs font-bold flex-shrink-0 mt-0.5 transition-colors cursor-pointer",
													activeCommentId === comment.id
														? "bg-brand-400 text-text-on-brand"
														: "bg-bg-sunken text-text-tertiary group-hover:bg-brand-100 group-hover:text-text-primary",
												)}
											>
												{idx + 1}
											</button>

											<div
												className="flex-1 min-w-0 cursor-pointer"
												onClick={() => handleFocusComment(comment)}
											>
												{/* Author + time */}
												<div className="flex items-center gap-2 mb-1">
													<span className="text-xs font-semibold text-text-secondary">
														{comment.userName ?? comment.userId}
													</span>
													<span className="text-2xs text-text-tertiary">
														{new Date(comment.createdAt).toLocaleTimeString(
															[],
															{
																hour: "2-digit",
																minute: "2-digit",
															},
														)}
													</span>
												</div>

												{/* Comment text or inline edit */}
												{editingCommentId === comment.id ? (
													<form
														onSubmit={(e) => {
															e.preventDefault();
															onSaveEdit();
														}}
														onClick={(e) => e.stopPropagation()}
														className="flex items-center gap-1.5 mt-0.5"
													>
														<Input
															type="text"
															value={editingText}
															onChange={(e) =>
																onEditingTextChange(e.target.value)
															}
															className="h-8 flex-1 px-2 py-1 text-xs"
															onKeyDown={(e) => {
																if (e.key === "Escape") {
																	onCancelEdit();
																}
															}}
														/>
														<Button
															type="submit"
															variant="secondary"
															size="icon-xs"
															disabled={!editingText.trim()}
														>
															<Check size={11} strokeWidth={2.5} />
														</Button>
													</form>
												) : (
													<p className="text-xs text-text-secondary leading-relaxed line-clamp-2">
														{comment.content}
													</p>
												)}
											</div>

											{/* Action buttons */}
											{editingCommentId !== comment.id && (
												<div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
													<Button
														variant="ghost"
														size="icon-xs"
														className="hover:text-success hover:bg-success-bg"
														onClick={(e) => {
															e.stopPropagation();
															handleResolveComment(comment.id);
														}}
														title="Resolve"
													>
														<Check size={12} />
													</Button>
												</div>
											)}
										</div>
									</div>
								))}
							</div>
						)}
					</div>

					{/* Sidebar Footer */}
					<div className="px-4 py-3 border-t border-border-subtle">
						<p className="text-2xs text-text-tertiary text-center">
							Click on the canvas to add a comment
						</p>
					</div>
				</motion.aside>
			)}
		</AnimatePresence>
	);
}
